import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';

import Layout from './AppLayout';
import Header from '../../../shared/components/layout/Header';
import Button from '../../../shared/components/ui/Button';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import CompanyCard from '../ui/CompanyCard';

import '../../styles/main.scss';
import Modal from '../../../shared/components/ui/Modal';
import AddCompany from '../forms/AddCompany';
import { useForm } from 'react-hook-form';
import { createCompany, getAllCompanies } from '../../services/api/implementation/impl';
import Loader from '../../../shared/components/layout/Loader';

import Cookies from 'js-cookie';
import NoDataFound from './NoDataFound';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { Plus } from 'lucide-react';

const CompaniesList = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();

  /* 
    Local state
  */
  const [openAddCompanyModal, setOpenAddCompanyModal] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [companiesToastShown, setCompaniesToastShown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { collapsed } = useSidebar();
  /* 
    Data fetching
  */
  const token = Cookies.get('token');
  const {
    isLoading,
    isError,
    data: companies,
    refetch: refetchCompanies
  } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getAllCompanies(),
    enabled: !!token
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Companies List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (token) {
      refetchCompanies();
    }
  }, [token, refetchCompanies]);

  // 1. Company List Viewed
  useEffect(() => {
    if (companies && companies.length > 0) {
      mixpanel.track('Company List Viewed', { company_count: companies.length });
    }
  }, [companies]);

  /* 
    Data mutation
  */
  const { mutate: mutateCompany, isLoading: isLoadingNewUserMutation } = useMutation({
    mutationFn: createCompany,
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (data) => {
      mixpanel.track('Company Created', { company_name: data?.company_name });
      toast.success('Company created successfully');
      reset();
      setOpenAddCompanyModal(false);
      setIsSubmitting(false);
      await refetchCompanies();
    },
    onError: (err) => {
      let errorMessage = 'An unexpected error occurred while creating the company.';
      setIsSubmitting(false);

      try {
        // Check if the error contains driverError details
        const detailMessage = err.response?.data?.message?.driverError?.detail;

        if (detailMessage && detailMessage.includes('Key (pan)=')) {
          // Extract PAN number using a regular expression
          const match = detailMessage.match(/Key \(pan\)=\((.*?)\)/);
          const panNumber = match?.[1]; // Extracted PAN number

          if (panNumber) {
            errorMessage = `PAN number ${panNumber} already exists.`;
          }
        } else if (err.message) {
          errorMessage = err.message; // Use general error message
        }
      } catch (error) {
        console.error('Error while parsing the error object:', error);
      }

      mixpanel.track('Company Creation Failed', { error: errorMessage });
      console.log('Final error message for toast:', errorMessage);
      toast.error(errorMessage);
    }
  });

  /* 
    Event handlers
  */
  const handleToggleAddCompanyModal = useCallback(() => {
    setOpenAddCompanyModal((prev) => {
      const willOpen = !prev;
      if (willOpen) {
        mixpanel.track('Add Company Modal Opened');
      } else {
        mixpanel.track('Add Company Modal Closed');
      }
      return willOpen;
    });
  }, []);

  const handleOnBack = useCallback(() => {
    navigate('/builder/company');
  }, [navigate]);

  const { reset } = useForm();

  useEffect(() => {
    setFilteredCompanies(companies);
  }, [companies]);

  useEffect(() => {
    if (openAddCompanyModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [openAddCompanyModal]);

  useEffect(() => {
    if (filteredCompanies?.length > 0 && !companiesToastShown) {
      // toast.success(`Successfully fetched ${filteredCompanies?.length} companies`);
      setCompaniesToastShown(true);
    }
  }, [filteredCompanies, companiesToastShown]);

  /* 
    Toasts
  */
  if (isLoading || isLoadingNewUserMutation) {
    return (
      <div className="h-screen w-screen">
        <Loader />
      </div>
    );
  }

  if (isError) {
    toast.error(`Error fetching companies`);
    navigate('/');
    return null;
  }

  // console.log(filteredCompanies?.length);

  /* 
    JSX
  */

  return (
    <>
      {openAddCompanyModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={56}
          name="Add Company"
          onClose={handleToggleAddCompanyModal}>
          <AddCompany
            onSubmit={mutateCompany}
            isSubmitting={isSubmitting}
            onClose={handleToggleAddCompanyModal}
          />
        </Modal>
      )}

      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header data={{ name: 'Company' }} onBack={handleOnBack} />
        </div>

        <div className="fixed bottom-8 right-8 z-50">
          <Button
            displayType="primary"
            onClick={() => {
              mixpanel.track('Add Company Button Clicked', {
                user_id: userId,
                timestamp: new Date().toISOString()
              });
              handleToggleAddCompanyModal();
            }}
            customStyle={{
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
            <Plus size={24} color="white" />
          </Button>
        </div>

        {companies && companies?.length > 0 ? (
          <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
            <GridWrapper width={100}>
              <div className="flex justify-start items-center gap-8 mt-[6rem] flex-wrap">
                {filteredCompanies?.length === 0 ? (
                  <div className="flex justify-center items-center w-full h-[300px]">
                    <p className="text-lg font-semibold text-stone-300">No data found</p>
                  </div>
                ) : (
                  filteredCompanies?.map((item) => (
                    <CompanyCard
                      key={item?.id}
                      data={item}
                      AllData={filteredCompanies}
                      refresh={refetchCompanies}
                    />
                  ))
                )}
              </div>
            </GridWrapper>
          </div>
        ) : (
          <NoDataFound onButtonClick={handleToggleAddCompanyModal} buttonText="Add Company" />
        )}
      </Layout>
    </>
  );
};

export default CompaniesList;
